import { useState } from 'react';
import { MapPin, Loader2, Square, Check, X, Moon } from 'lucide-react';
import { suggestPoints, hourFac } from '../lib/aquaLeakFind';
import { cancelSim } from '../lib/aquaNet';
import { ProfileBox } from './AquaProfile';
import { useAqua, setAqua } from '../lib/aquaStore';

const nf = (v, d = 2) => (v == null || !Number.isFinite(+v) ? '–' : (+v).toFixed(d).replace('.', ','));
let stopFlag = false;

// measuring time: night hours lower the consumption, so small leaks stand out
export function NightBox({ t, ls, setLs, cfg, setCfg }) {
  const day = ls.day === 'we' ? 'we' : 'wd', P = Array.from({ length: 24 }, (_, i) => hourFac(cfg, day, i)), h = ls.hour ?? '', f = h === '' ? null : P[+h];
  return (
    <div className="lf-night" data-testid="lf-night">
      <label className="aq-row"><Moon size={14} /> {t.lfTime}
        <select data-testid="lf-hour" value={h} onChange={(e) => setLs({ hour: e.target.value === '' ? null : +e.target.value, fac: '' })}>
          <option value="">{t.lfTimeDay}</option>
          {P.map((x, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00 · {nf(x)}×</option>)}
        </select>
      </label>
      <div className="row" data-testid="lf-day">
        {['wd', 'we'].map((k) => <button key={k} className={`chip seg ${day === k ? 'on' : ''}`} data-testid={`lf-day-${k}`} onClick={() => setLs({ day: k, fac: '' })}>{t['lfDay_' + k]}</button>)}
      </div>
      {h !== '' && <label className="aq-row">{t.lfFac}<input type="number" step="0.05" min="0" data-testid="lf-fac" value={ls.fac ?? ''} placeholder={nf(f).replace(',', '.')} onChange={(e) => setLs({ fac: e.target.value })} />× </label>}
      <ProfileBox t={t} cfg={cfg} setCfg={setCfg} day={day} />
      <small className="muted">{h === '' ? t.lfNightHint : t.lfNightOn.replace('{h}', String(h).padStart(2, '0')).replace('{f}', nf(ls.fac !== '' && ls.fac != null ? +ls.fac : f))}</small>
    </div>
  );
}

// which hydrants to measure for the leak search
export function MeasPlan({ t, project, cfg, ls, setLs, qGuess }) {
  const s = useAqua(), [busy, setBusy] = useState(0), k = ls.k ?? 6;
  const run = async () => {
    stopFlag = false; setBusy(1);
    const r = await suggestPoints(project, cfg, Math.max(1, Math.min(20, +k || 6)), qGuess > 0.2 ? qGuess : 2, (n) => setBusy(n + 1), () => stopFlag);
    if (r.error !== 'cancel') setAqua({ mp: r });
    setBusy(0);
  };
  const take = () => setLs({ hyd: Object.fromEntries(s.mp.pts.map((p) => [p.id, ls.hyd?.[p.id] ?? ''])) });
  const mp = s.mp;
  return (
    <div className="lf-plan" data-testid="mp-panel">
      <b className="aq-sub">{t.mpTitle}</b>
      <small className="muted">{t.mpIntro}</small>
      <div className="row">
        <label className="aq-row">{t.mpCount}<input type="number" min="1" max="20" data-testid="mp-count" value={k} onChange={(e) => setLs({ k: e.target.value })} /></label>
        <button className="btn btn-outline btn-sm" data-testid="mp-run" disabled={!!busy} onClick={run}>{busy ? <Loader2 size={14} className="spin" /> : <MapPin size={14} />} {busy ? `${t.lfBusy} ${busy - 1}/16` : t.mpRun}</button>
        {busy > 0 && <button className="btn btn-outline btn-sm" data-testid="mp-cancel" onClick={() => { stopFlag = true; cancelSim(); }}><Square size={14} /> {t.aqCancel}</button>}
      </div>
      {mp?.error && <p className="aq-err" data-testid="mp-error">{t['mpErr_' + mp.error] || t.aqErr_calc}</p>}
      {mp?.pts && (
        <div data-testid="mp-result">
          <small className="muted" data-testid="mp-cover">{t.mpCover.replace('{p}', nf(mp.cover * 100, 0)).replace('{q}', nf(mp.q, 1))}</small>
          <ol className="lf-top">{mp.pts.map((p, i) => (
            <li key={p.id}><button className="lf-hit" data-testid={`mp-pt-${i}`} onClick={() => window.__gissMapView?.()?.m?.setView([p.lat, p.lon], 18)}>
              <i style={{ background: '#7c3aed' }} /><span>{p.name || p.id}</span><b>Ø {nf(p.sens * 1000, 0)} · max {nf(p.max * 1000, 0)} mbar</b>
            </button></li>))}</ol>
          <div className="row">
            <button className="btn btn-accent btn-sm" data-testid="mp-take" onClick={take}><Check size={14} /> {t.mpTake}</button>
            <button className="btn btn-outline btn-sm" data-testid="mp-clear" onClick={() => setAqua({ mp: null })}><X size={14} /> {t.lfClear}</button>
          </div>
        </div>
      )}
    </div>
  );
}
